import { useReducer } from "react";
import { useTranslation } from "react-i18next";
import { editMemory, type Memory } from "../../../api/threads/memoriesApi";
import { notifyMemoriesChanged } from "../memoryEvents";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { transitionWordingEdit, wordingDraftOf } from "./wordingEdit";

const MAXIMUM_TITLE = 120;
const MAXIMUM_NOTE = 1000;

/**
 * A memory's title and note, edited in place where it is used: its Thread, the Thread it was
 * kept from, or a person's own list (ADR-0031 D5: "memory edits are possible through
 * accessing them from the respective thread or workflow views"). Only whoever may change it
 * sees Edit; an edit keeps its state (MEM-5). `readOnly`: another place owns the wording now,
 * such as the memory's review step (OW-07). It holds for an editor already open too: it closes
 * at once and sends nothing more. A save already sent still lands; if it fails, or none was sent,
 * the unsaved draft stays in view until the person discards it.
 */
export default function MemoryWording({ projectId, memory, onChanged, readOnly = false }: { projectId: string; memory: Memory; onChanged: () => void; readOnly?: boolean }) {
  const { t } = useTranslation();
  const [wording, dispatch] = useReducer(transitionWordingEdit, { status: "shown" });
  const { state, submit } = useSubmission();
  const saving = state.status === "submitting";
  // The editor gives way at once (state adjusted while rendering).
  if (readOnly && wording.status === "editing") {
    dispatch({ type: "handedOver", memory });
  }

  if (wording.status !== "editing") {
    return (
      <div className="space-y-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="app-title text-sm font-semibold">{memory.title}</span>
          {memory.mayChange && !readOnly && wording.status === "shown" ? (
            <AppButton variant="ghost" size="xs" onClick={() => { dispatch({ type: "opened", draft: wordingDraftOf(memory) }); }}>{t("memories.wording.edit")}</AppButton>
          ) : null}
        </div>
        {memory.note === null ? null : <p className="text-sm">{memory.note}</p>}
        {/* While a save sent before the hand-over is pending, the draft may still land. */}
        {wording.status === "displaced" && !saving ? (
          <div className="app-card-muted space-y-1 p-2" role="status">
            <p className="app-text-muted text-xs">{t("memories.wording.notSaved")}</p>
            <p className="text-sm font-semibold">{wording.draft.title}</p>
            {wording.draft.note.trim() === "" ? null : <p className="text-sm">{wording.draft.note}</p>}
            <AppButton variant="ghost" size="xs" onClick={() => { dispatch({ type: "closed" }); }}>{t("memories.wording.discardDraft")}</AppButton>
          </div>
        ) : null}
      </div>
    );
  }
  const { title, note } = wording.draft;
  const save = () => {
    submit(async () => {
      await editMemory(projectId, memory.memoryId, { title: title.trim(), note: note.trim() === "" ? null : note.trim() });
      dispatch({ type: "saved" });
      onChanged();
      notifyMemoriesChanged();
    });
  };
  return (
    <div className="space-y-2">
      <Field label={t("memories.keep.title")}>
        {(control) => <input {...control} className={FORM_CONTROL_CLASS} value={title} maxLength={MAXIMUM_TITLE} disabled={saving} onChange={(event) => { dispatch({ type: "changed", draft: { title: event.target.value, note } }); }} />}
      </Field>
      <Field label={t("memories.keep.note")}>
        {(control) => <textarea {...control} className={FORM_CONTROL_CLASS} rows={3} value={note} maxLength={MAXIMUM_NOTE} disabled={saving} onChange={(event) => { dispatch({ type: "changed", draft: { title, note: event.target.value } }); }} />}
      </Field>
      {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      <div className="flex gap-2">
        <AppButton variant="primary" size="sm" disabled={saving || title.trim() === ""} onClick={save}>{t("memories.wording.save")}</AppButton>
        <AppButton variant="secondary" size="sm" disabled={saving} onClick={() => { dispatch({ type: "closed" }); }}>{t("common.cancel")}</AppButton>
      </div>
    </div>
  );
}
